// The bar's slider, web | hotty | tui (sitebar -modes): a radio group,
// which a site whose bar has it wires here. A click, or the keys, choose a
// mode, and the site is told, to show it and keep it (tui.ts); the site
// says which is checked, as what it shows may not be what was chosen (a
// terminal on its way, a mode the page doesn't have). The keyboard takes
// the group as one stop, the checked radio: the arrows choose the one
// before or after, round the ends, and Home and End the first and the
// last. A mode a page doesn't have (disable) is shown, and skipped.

import type { Mode } from "./tui";

const radios = () => Array.from(document.querySelectorAll<HTMLButtonElement>(".npio-modes [data-mode]"));
const enabled = (b: HTMLElement) => b.getAttribute("aria-disabled") !== "true";

/** Says which mode is checked, and whether it is on its way (busy): the
 * checked radio is the keyboard's stop. */
export function check(mode: Mode, busy = false) {
  for (const b of radios()) {
    const on = b.dataset.mode === mode;
    b.setAttribute("aria-checked", String(on));
    b.tabIndex = on ? 0 : -1;
    if (busy && on) b.setAttribute("aria-busy", "true");
    else b.removeAttribute("aria-busy");
  }
}

/** Makes mode no choice on this page, title saying why. */
export function disable(mode: Mode, title: string) {
  for (const b of radios()) {
    if (b.dataset.mode !== mode) continue;
    b.dataset.title ??= b.title;
    b.setAttribute("aria-disabled", "true");
    b.title = title;
  }
}

/** Makes mode a choice again, as the bar has it: where a site's pages
 * differ in what they have, and the reader goes from one to the next
 * without leaving the document. */
export function enable(mode: Mode) {
  for (const b of radios()) {
    if (b.dataset.mode !== mode || b.getAttribute("aria-disabled") !== "true") continue;
    b.removeAttribute("aria-disabled");
    b.title = b.dataset.title ?? b.title;
  }
}

/** Calls chosen with the mode the reader chooses, and whether a pointer
 * chose it (a click's, not a key's). */
export function slider(chosen: (mode: Mode, pointer: boolean) => void) {
  const all = radios();
  for (const b of all) {
    b.addEventListener("click", (e) => {
      if (enabled(b)) chosen(b.dataset.mode as Mode, e.detail > 0);
    });
    b.addEventListener("keydown", (e) => {
      const on = all.filter(enabled);
      const i = on.indexOf(b);
      const to = { ArrowLeft: i - 1, ArrowUp: i - 1, ArrowRight: i + 1, ArrowDown: i + 1, Home: 0, End: on.length - 1 }[e.key];
      if (to === undefined || !on.length) return;
      e.preventDefault();
      const next = on[(to + on.length) % on.length];
      next.focus();
      chosen(next.dataset.mode as Mode, false);
    });
  }
}
